import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ShieldCheck, HandCoins, ChevronDown, ChevronRight, Hourglass, Wallet, CheckCircle2 } from "lucide-react";
import { fmtDate, formatIDR } from "@/lib/constants";
import api from "@/lib/api";

// 2026-10-14: label opsi Sub Project dengan disambiguasi WO/VO.
function subOptionLabel(p) {
  if (p.project_type === "working_order") return `WO · ${p.wo_number || p.project_name || "-"}`;
  if (p.project_type === "variation_order") return `VO · ${p.variation_order_name || p.project_name || "-"}`;
  return p.project_name || "-";
}

/**
 * Retensi & Collection Tab — 149-cw-r121 (2026-08-29 update).
 * READ-ONLY. Rumus yang dipakai (konsisten antara card & tabel):
 *   Retensi Ditahan  = SUM(income_entries.retention_amount) untuk entry non-claim
 *   Sudah Diklaim    = SUM(income_entries.amount) untuk entry is_retention_claim
 *   Klaim Dibayar    = SUM(is_retention_claim.amount) untuk entry yang punya `date`
 *   Belum Diklaim    = Retensi Ditahan − Klaim Dibayar (bukan − Sudah Diklaim,
 *                       karena klaim yang belum dibayar belum keluar dari retensi)
 */
export default function RetensiCollectionTab({ projects, invoiceRows, loading }) {
  // 2026-10-14 (user request): filter Induk Project → Sub Project (pola Invoice & Piutang).
  const [parentId, setParentId] = useState("all");
  const [subId, setSubId] = useState("all");
  // 2026-10-18 (user request "SAMAKAN DATA PIUTANG"): kartu retensi memakai
  // klasifikasi engine A.R. (/ar/outstanding-report) — SAMA dengan Dasbor,
  // Collection, dan Monitoring Proyek. Scope induk/sub diterapkan client-side
  // ke retention_rows (head_id/project_id).
  const [arReport, setArReport] = useState(null);
  useEffect(() => {
    api.get("/ar/outstanding-report").then(({ data }) => setArReport(data)).catch(() => setArReport(null));
  }, []);
  const engRows = useMemo(() => {
    const rows = arReport?.retention_rows || [];
    if (parentId === "all" && subId === "all") return rows;
    return rows.filter((r) => (subId !== "all" ? r.project_id === subId : r.head_id === parentId));
  }, [arReport, parentId, subId]);
  const eng = useMemo(() => {
    const nc = engRows.filter((r) => r.kind === "not_claimed");
    const cl = engRows.filter((r) => r.kind !== "not_claimed");
    const sum = (xs, f) => xs.reduce((s, x) => s + (Number(f(x)) || 0), 0);
    return {
      held: sum(nc, (x) => x.amount) + sum(cl, (x) => x.cash_due),
      notClaimed: sum(nc, (x) => x.amount),
      waiting: sum(cl.filter((x) => x.claim_state === "menunggu_pembayaran"), (x) => x.amount),
      partial: sum(cl.filter((x) => x.claim_state === "dibayar_sebagian"), (x) => x.amount),
      paid: sum(cl.filter((x) => x.claim_state === "dicairkan"), (x) => x.cash_due),
    };
  }, [engRows]);
  const parentOpts = useMemo(() => {
    const ids = new Set((projects || []).map((p) => p.id));
    return (projects || []).filter((p) => !p.parent_project_id || !ids.has(p.parent_project_id));
  }, [projects]);
  const subOpts = useMemo(() => (
    parentId === "all" ? [] : (projects || []).filter((p) => p.parent_project_id === parentId)
  ), [projects, parentId]);

  // Baris ter-scope filter induk/sub — kartu KPI & tabel memakai sumber yang sama.
  const scopedRows = useMemo(() => {
    if (parentId === "all" && subId === "all") return invoiceRows;
    return invoiceRows.filter((r) => {
      const p = r.__project;
      if (subId !== "all") return p?.id === subId;
      const head = p?.parent_project_id || p?.id;
      return head === parentId;
    });
  }, [invoiceRows, parentId, subId]);

  const summary = useMemo(() => {
    const claims = scopedRows.filter((r) => r.is_retention_claim);
    const totalClaimed = claims.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const totalClaimedPaid = claims.reduce((s, r) => s + (r.date ? Number(r.amount) || 0 : 0), 0);
    const totalHeld = scopedRows
      .filter((r) => !r.is_retention_claim)
      .reduce((s, r) => s + (Number(r.retention_amount) || 0), 0);
    const outstanding = Math.max(0, totalHeld - totalClaimedPaid);
    return { claims, totalClaimed, totalClaimedPaid, totalHeld, outstanding };
  }, [scopedRows]);

  const retentionByProject = useMemo(() => {
    const map = new Map();
    for (const r of scopedRows) {
      if (r.is_retention_claim) continue;
      const heldAmt = Number(r.retention_amount) || 0;
      if (heldAmt <= 0) continue;
      const pid = r.__project?.id;
      if (!pid) continue;
      const prev = map.get(pid) || { project: r.__project, held: 0, count: 0 };
      prev.held += heldAmt;
      prev.count += 1;
      map.set(pid, prev);
    }
    return Array.from(map.values()).sort((a, b) => b.held - a.held);
  }, [scopedRows]);

  // 2026-06-11 (user request): pengelompokan per induk + chevron breakdown,
  // konsisten dgn tab Ringkasan / Invoice & Piutang / Pembayaran Masuk.
  const [expandedRet, setExpandedRet] = useState(() => new Set());
  const [expandedClaims, setExpandedClaims] = useState(() => new Set());
  const toggleSet = (setter) => (id) => setter((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleRet = toggleSet(setExpandedRet);
  const toggleClaim = toggleSet(setExpandedClaims);

  // Grup "Retensi Ditahan per Project" per induk — header = gabungan parent+anak.
  const retentionGroups = useMemo(() => {
    const byId = new Map(projects.map((p) => [p.id, p]));
    const m = new Map();
    for (const r of retentionByProject) {
      const headId = r.project.parent_project_id || r.project.id;
      if (!m.has(headId)) m.set(headId, { headId, head: byId.get(headId) || r.project, items: [] });
      m.get(headId).items.push(r);
    }
    return Array.from(m.values()).map((g) => ({
      ...g,
      held: g.items.reduce((s, x) => s + x.held, 0),
      count: g.items.reduce((s, x) => s + x.count, 0),
      expandable: g.items.length > 1 || g.items.some((x) => x.project.id !== g.head.id),
    })).sort((a, b) => b.held - a.held);
  }, [retentionByProject, projects]);

  // Grup "Riwayat Klaim Retensi" per induk.
  const claimGroups = useMemo(() => {
    const byId = new Map(projects.map((p) => [p.id, p]));
    const claims = [...summary.claims].sort((a, b) => (b.invoice_date || "").localeCompare(a.invoice_date || ""));
    const m = new Map();
    for (const r of claims) {
      const headId = r.__project?.parent_project_id || r.__project?.id || "unknown";
      if (!m.has(headId)) m.set(headId, { headId, head: byId.get(headId) || r.__project, rows: [] });
      m.get(headId).rows.push(r);
    }
    return Array.from(m.values()).map((g) => ({
      ...g,
      total: g.rows.reduce((s, x) => s + (Number(x.amount) || 0), 0),
      expandable: g.rows.length > 1,
    }));
  }, [summary.claims, projects]);

  if (loading && invoiceRows.length === 0) {
    return <div className="p-8 text-center text-xs italic text-slate-400">Memuat retensi…</div>;
  }

  return (
    <div className="space-y-4" data-testid="fin-retensi-tab">
      {/* KPI cards — 2026-10-18: LIMA sub-status retensi dari engine A.R.
          (klasifikasi sama dgn Dasbor/Collection/Monitoring). Fallback ke
          perhitungan lama bila engine belum termuat. */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        <RetCard testid="fin-ret-held" label="Retensi Masih Ditahan" hint="Retensi inside pada invoice reguler (belum dikurangi rilis)" value={arReport ? eng.held : summary.totalHeld} icon={<ShieldCheck size={16} className="text-sky-700" />} tone="sky" />
        <RetCard testid="fin-ret-notclaimed" label="Retensi Belum Diklaim" hint="Ditahan − dirilis (engine A.R.)" value={arReport ? eng.notClaimed : summary.outstanding} icon={<Hourglass size={16} className="text-orange-700" />} tone="orange" />
        <RetCard testid="fin-ret-claim-waiting" label="Klaim Menunggu Pembayaran" hint="Klaim retensi belum menerima pembayaran" value={arReport ? eng.waiting : 0} icon={<Wallet size={16} className="text-amber-700" />} tone="amber" />
        <RetCard testid="fin-ret-claim-partial" label="Klaim Dibayar Sebagian" hint="Sisa klaim retensi setelah pembayaran parsial" value={arReport ? eng.partial : 0} icon={<HandCoins size={16} className="text-indigo-700" />} tone="sky" />
        <RetCard testid="fin-ret-paid" label="Retensi Sudah Dicairkan" hint="Klaim retensi dibayar penuh" value={arReport ? eng.paid : summary.totalClaimedPaid} icon={<CheckCircle2 size={16} className="text-emerald-700" />} tone="emerald" />
      </div>

      {/* 2026-10-14 (user request): filter Induk → Sub Project. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="fin-ret-filter-bar">
        <select value={parentId} onChange={(e) => { setParentId(e.target.value); setSubId("all"); }}
          data-testid="fin-ret-parent-filter"
          className="h-9 px-2 text-[11px] border border-slate-300 bg-white text-slate-700 focus:outline-none focus:border-[#0B1D3A] max-w-[240px]">
          <option value="all">Semua Induk Project</option>
          {parentOpts.map((p) => <option key={p.id} value={p.id}>{p.project_name || "-"}</option>)}
        </select>
        <select value={subId} onChange={(e) => setSubId(e.target.value)} disabled={parentId === "all"}
          data-testid="fin-ret-sub-filter"
          className="h-9 px-2 text-[11px] border border-slate-300 bg-white text-slate-700 focus:outline-none focus:border-[#0B1D3A] max-w-[240px] disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed">
          <option value="all">{parentId === "all" ? "Pilih Induk dulu" : "Semua Sub Project"}</option>
          {subOpts.map((p) => <option key={p.id} value={p.id}>{subOptionLabel(p)}</option>)}
        </select>
        {(parentId !== "all" || subId !== "all") && (
          <button type="button" onClick={() => { setParentId("all"); setSubId("all"); }}
            data-testid="fin-ret-filter-reset"
            className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-rose-600">Reset</button>
        )}
      </div>

      {/* Retensi per project — dikelompokkan per induk (2026-06-11). */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Retensi Ditahan per Project</div>
          {retentionGroups.some((g) => g.expandable) && (
            <div className="flex gap-2" data-testid="fin-ret-expand-controls">
              <button type="button" onClick={() => setExpandedRet(new Set(retentionGroups.filter((g) => g.expandable).map((g) => g.headId)))}
                data-testid="fin-ret-expand-all"
                className="text-[10px] uppercase tracking-wider font-semibold text-[#0B1D3A] hover:underline">Buka Semua Rincian</button>
              <button type="button" onClick={() => setExpandedRet(new Set())}
                data-testid="fin-ret-collapse-all"
                className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:underline">Tutup Semua</button>
            </div>
          )}
        </div>
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b-2 border-slate-300">
              <tr className="text-left">
                <th className="px-3 py-2 overline text-slate-700">Project</th>
                <th className="px-3 py-2 overline text-slate-700">Client</th>
                <th className="px-3 py-2 overline text-slate-700 text-right"># Invoice</th>
                <th className="px-3 py-2 overline text-slate-700 text-right">Retensi Ditahan</th>
                <th className="px-3 py-2 overline text-slate-700">Retensi %</th>
                <th className="px-3 py-2 overline text-slate-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {retentionGroups.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-slate-400 italic">Tidak ada retensi ditahan aktif.</td></tr>
              )}
              {retentionGroups.map((g) => {
                const isOpen = expandedRet.has(g.headId);
                return (
                  <Fragment key={g.headId}>
                    {/* Baris induk — retensi gabungan parent + VO/WO. */}
                    <tr onClick={() => g.expandable && toggleRet(g.headId)}
                      className={`border-b border-slate-100 hover:bg-slate-50 ${g.expandable ? "cursor-pointer" : ""}`}
                      data-testid={`fin-ret-group-${g.headId}`}>
                      <td className="px-3 py-2 text-slate-800 font-semibold">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {g.expandable ? (
                            <button type="button"
                              onClick={(e) => { e.stopPropagation(); toggleRet(g.headId); }}
                              data-testid={`fin-ret-group-expand-${g.headId}`}
                              className="shrink-0 p-0.5 border border-slate-300 text-slate-500 hover:text-[#0B1D3A] hover:border-[#0B1D3A]"
                              title={isOpen ? "Tutup rincian VO/WO" : "Lihat rincian VO/WO"}>
                              {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                            </button>
                          ) : <span className="w-[18px] shrink-0" />}
                          <span className="truncate max-w-[240px]" title={g.head.project_name}>{g.head.project_name || g.head.wo_number}</span>
                          {g.items.some((x) => x.project.id !== g.head.id) && (
                            <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-px bg-[#0B1D3A] text-white" data-testid={`fin-ret-group-kids-${g.headId}`}>
                              Main + {g.items.filter((x) => x.project.id !== g.head.id).length} Sub
                            </span>
                          )}
                          {g.head.vo_aggregation === "combined" && <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-px bg-violet-100 text-violet-800 border border-violet-300">VO Gabung</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{g.head.client || "-"}</td>
                      <td className="px-3 py-2 tabular-nums text-right">{g.count}</td>
                      <td className="px-3 py-2 tabular-nums text-right font-bold text-sky-700">{formatIDR(g.held)}</td>
                      <td className="px-3 py-2 tabular-nums text-slate-700">{(Number(g.head.retention_pct) || 0).toFixed(1)}%</td>
                      <td className="px-3 py-2">
                        <span className="inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border border-slate-300 bg-slate-50 text-slate-600">
                          {g.head.status || "aktif"}
                        </span>
                      </td>
                    </tr>
                    {/* Baris anak (VO/WO) — retensi per sub-project. */}
                    {isOpen && g.items.filter((x) => x.project.id !== g.head.id).map((x) => {
                      const ptype = x.project.project_type;
                      return (
                        <tr key={x.project.id} className="border-b border-slate-100 hover:bg-slate-100 bg-slate-50/50" data-testid={`fin-ret-child-${x.project.id}`}>
                          <td className="px-3 py-1.5 text-slate-700">
                            <div className="flex items-center gap-1.5 pl-6">
                              <span className={`shrink-0 text-[9px] uppercase tracking-wider font-bold px-1 py-px border ${ptype === "variation_order" ? "bg-violet-50 text-violet-700 border-violet-300" : ptype === "working_order" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                                {ptype === "variation_order" ? "VO" : ptype === "working_order" ? "WO" : "SUB"}
                              </span>
                              <span className="truncate">{x.project.variation_order_name || x.project.wo_number || x.project.project_name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-1.5 text-slate-500">{x.project.client || "-"}</td>
                          <td className="px-3 py-1.5 tabular-nums text-right">{x.count}</td>
                          <td className="px-3 py-1.5 tabular-nums text-right font-semibold text-sky-700">{formatIDR(x.held)}</td>
                          <td className="px-3 py-1.5 tabular-nums text-slate-600">{(Number(x.project.retention_pct) || 0).toFixed(1)}%</td>
                          <td className="px-3 py-1.5">
                            <span className="inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border border-slate-300 bg-slate-50 text-slate-600">
                              {x.project.status || "aktif"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Retention claims history — dikelompokkan per induk (2026-06-11). */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Riwayat Klaim Retensi</div>
          {claimGroups.some((g) => g.expandable) && (
            <div className="flex gap-2" data-testid="fin-ret-claim-expand-controls">
              <button type="button" onClick={() => setExpandedClaims(new Set(claimGroups.filter((g) => g.expandable).map((g) => g.headId)))}
                data-testid="fin-ret-claim-expand-all"
                className="text-[10px] uppercase tracking-wider font-semibold text-[#0B1D3A] hover:underline">Buka Semua Rincian</button>
              <button type="button" onClick={() => setExpandedClaims(new Set())}
                data-testid="fin-ret-claim-collapse-all"
                className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:underline">Tutup Semua</button>
            </div>
          )}
        </div>
        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b-2 border-slate-300">
              <tr className="text-left">
                <th className="px-3 py-2 overline text-slate-700">Tanggal Klaim</th>
                {/* 2026-02-13 (user request): kolom Jatuh Tempo + badge lewat tempo. */}
                <th className="px-3 py-2 overline text-slate-700">Jatuh Tempo</th>
                <th className="px-3 py-2 overline text-slate-700">Project</th>
                <th className="px-3 py-2 overline text-slate-700 text-right">Nilai Klaim</th>
                <th className="px-3 py-2 overline text-slate-700">Status</th>
                <th className="px-3 py-2 overline text-slate-700">Catatan</th>
              </tr>
            </thead>
            <tbody>
              {claimGroups.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-slate-400 italic">Belum ada klaim retensi.</td></tr>
              )}
              {claimGroups.map((g) => {
                const isOpen = expandedClaims.has(g.headId);
                return (
                  <Fragment key={g.headId}>
                    {g.expandable ? (
                      <tr onClick={() => toggleClaim(g.headId)}
                        className="border-b border-slate-200 bg-slate-50/70 hover:bg-slate-100 cursor-pointer"
                        data-testid={`fin-ret-claim-group-${g.headId}`}>
                        <td className="px-3 py-2">
                          <button type="button"
                            onClick={(e) => { e.stopPropagation(); toggleClaim(g.headId); }}
                            data-testid={`fin-ret-claim-group-expand-${g.headId}`}
                            className="p-0.5 border border-slate-300 text-slate-500 hover:text-[#0B1D3A] hover:border-[#0B1D3A]"
                            title={isOpen ? "Tutup rincian klaim" : "Lihat rincian klaim"}>
                            {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-slate-800">
                          <div className="font-semibold flex items-center gap-1.5 flex-wrap">
                            <span className="truncate max-w-[240px]" title={g.head?.project_name}>{g.head?.project_name || g.head?.wo_number || "-"}</span>
                            <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-px bg-[#0B1D3A] text-white" data-testid={`fin-ret-claim-kids-${g.headId}`}>{g.rows.length} Klaim</span>
                          </div>
                          {g.head?.client && <div className="text-[10px] text-slate-500">{g.head.client}</div>}
                        </td>
                        <td className="px-3 py-2 text-slate-400">—</td>
                        <td className="px-3 py-2 tabular-nums text-right font-bold text-amber-700">{formatIDR(g.total)}</td>
                        <td className="px-3 py-2 text-slate-400">—</td>
                        <td className="px-3 py-2 text-slate-400">—</td>
                      </tr>
                    ) : null}
                    {(!g.expandable || isOpen) && g.rows.map((r) => (
                      <ClaimRow key={r.id} r={r} indent={g.expandable} />
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 flex items-center justify-between" data-testid="fin-collection-linkbar">
        <div>Butuh tindak lanjut follow-up piutang? Fitur <b>Collection & Retention</b> tetap tersedia di sidebar.</div>
        <Link to="/collection" className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white text-[10px] uppercase tracking-wider font-semibold">
          <ExternalLink size={11} /> Buka Collection
        </Link>
      </div>
    </div>
  );
}

/**
 * ClaimRow — satu baris klaim retensi (grup tunggal maupun rincian expand).
 * 2026-06-11.
 */
function ClaimRow({ r, indent = false }) {
  // 2026-02-13 (user request): kolom Jatuh Tempo — badge merah bila lewat tempo &
  // belum dibayar (backend mengirim notif ke tim FAT utk kasus ini), amber bila
  // jatuh tempo hari ini.
  const due = (r.due_date || r.invoice_due_date || "").slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const unpaid = !r.date;
  const overdueDays = (unpaid && due && due < today)
    ? Math.round((new Date(today) - new Date(due)) / 86400000)
    : 0;
  const dueToday = unpaid && due && due === today;
  return (
    <tr className={`border-b border-slate-100 hover:bg-slate-50 ${indent ? "bg-slate-50/50" : ""}`} data-testid={`fin-ret-claim-${r.id}`}>
      <td className="px-3 py-2 tabular-nums whitespace-nowrap text-slate-700">{r.invoice_date ? fmtDate(r.invoice_date) : "-"}</td>
      <td className="px-3 py-2 tabular-nums whitespace-nowrap" data-testid={`fin-ret-claim-due-${r.id}`}>
        {due ? (
          <span className={overdueDays > 0 ? "text-rose-700 font-semibold" : "text-slate-700"}>
            {fmtDate(due)}
            {overdueDays > 0 && (
              <span className="ml-1.5 inline-block text-[9px] uppercase tracking-wider font-bold px-1 py-px border border-rose-300 bg-rose-50 text-rose-700">
                Lewat {overdueDays} hari
              </span>
            )}
            {dueToday && (
              <span className="ml-1.5 inline-block text-[9px] uppercase tracking-wider font-bold px-1 py-px border border-amber-300 bg-amber-50 text-amber-800">
                Jatuh tempo hari ini
              </span>
            )}
          </span>
        ) : <span className="text-slate-400">-</span>}
      </td>
      <td className={`px-3 py-2 text-slate-800 font-semibold ${indent ? "pl-8" : ""}`}>{r.__project?.project_name || "-"}</td>
      <td className="px-3 py-2 tabular-nums text-right font-bold text-amber-700">{formatIDR(r.amount)}</td>
      <td className="px-3 py-2">
        {r.date ? (
          <span className="inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-800">Dibayar</span>
        ) : (
          <span className="inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-800">Waiting</span>
        )}
      </td>
      <td className="px-3 py-2 text-slate-600 max-w-[280px] truncate" title={r.note}>{r.note || "-"}</td>
    </tr>
  );
}

function RetCard({ label, hint, value, icon, tone, testid }) {  const tc = {
    sky: "border-l-sky-500", amber: "border-l-amber-500",
    emerald: "border-l-emerald-500", rose: "border-l-rose-500",
  }[tone] || "border-l-slate-400";
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${tc} p-3`} data-testid={testid}>
      <div className="flex items-center gap-1.5 overline text-slate-600">{icon} {label}</div>
      <div className="font-display text-lg font-bold text-[#0B1D3A] tabular-nums mt-0.5">{formatIDR(value)}</div>
      {hint && <div className="text-[9px] text-slate-400 mt-0.5 italic">{hint}</div>}
    </div>
  );
}
